/**
 * Authentication utilities
 * Handles token storage, user session, and language preference loading
 */

import axiosInstance from '../api/axios';
import i18n from '../i18n';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  language: 'uz' | 'ru' | 'en';
  role?: string;
  isSuperadmin?: boolean;
}

interface LoginResponse {
  token: string;
  user: User;
  workspaces: Array<{
    id: string;
    name: string;
    role: string;
  }>;
  requiresOtp?: boolean;
  userId?: string;
  email?: string;
  message?: string;
}

/**
 * Login user and load language preference
 * @param email - User email
 * @param password - User password
 * @returns Login response with token and user data
 */
export const login = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await axiosInstance.post<LoginResponse>('/auth/login', {
      email,
      password,
      language: i18n.language || 'ru', // Send current UI language
    });

    // Check if OTP is required
    if (response.data.requiresOtp) {
      // Return early without storing token or loading language
      return response.data;
    }

    const { token, user } = response.data;

    // Store token
    if (token) {
      localStorage.setItem('token', token);
    }

    // Load user's language preference
    if (user && user.language) {
      await i18n.changeLanguage(user.language);
    }

    return response.data;
  } catch (error: any) {
    console.error('Login error:', error);
    console.error('Response data:', error.response?.data);
    console.error('Response status:', error.response?.status);
    console.error('Response headers:', error.response?.headers);
    throw error;
  }
};

/**
 * Logout user and clear session
 */
export const logout = async (): Promise<void> => {
  const token = localStorage.getItem('token');

  if (token) {
    try {
      await axiosInstance.post('/auth/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    }
  }

  // Clear local storage
  localStorage.removeItem('token');
};

/**
 * Load current user and their language preference
 * @returns User data
 */
export const loadCurrentUser = async (): Promise<User | null> => {
  const token = localStorage.getItem('token');

  if (!token) {
    return null;
  }

  try {
    const response = await axiosInstance.get('/auth/me');

    const { user } = response.data;

    // Load user's language preference
    if (user.language) {
      await i18n.changeLanguage(user.language);
    }

    return user;
  } catch (error) {
    console.error('Failed to load current user:', error);
    // Clear invalid token
    localStorage.removeItem('token');
    return null;
  }
};

/**
 * Check if user is authenticated
 * @returns True if user has valid token
 */
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('token');
};
